import { Core } from "@faire/design-tokens";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import React from "react";
import styled, { keyframes } from "styled-components";

const TRANSITION_SPEED_MS = "300ms";
const BADGE_SIZE = 20;
const ICON_SIZE = 40;

interface IconWithCountDotProps {
  icon: React.ReactNode;
  count: string | undefined;
}

export const IconWithCountBadge: React.FC<IconWithCountDotProps> = ({
  icon,
  count,
}) => {
  const hideBadge = count === undefined || count === "0";
  // Add spacing of 7px per digit in count to keep consistent spacing between icon and badge
  const spacerSize = count?.length ? (count?.length - 1) * 7 : 0;

  return (
    <Row>
      {!hideBadge ? (
        <IconCountBadgeWrapper>
          <IconCountBadge>
            <StyledTypography variant="labelSansMedium">
              {count}
            </StyledTypography>
          </IconCountBadge>
        </IconCountBadgeWrapper>
      ) : null}

      <IconWrapper align="center" justify="center">
        {icon}
      </IconWrapper>
      <Spacer width={spacerSize} />
    </Row>
  );
};

const IconWrapper = styled(Row)`
  &:hover {
    background-color: ${Core.surface.subdued};
  }
  height: ${ICON_SIZE}px;
  width: ${ICON_SIZE}px;

  border-radius: 50%;
  transition: all ${TRANSITION_SPEED_MS} ease-out;
`;

const IconCountBadgeWrapper = styled(Row)`
  position: absolute;
  min-width: ${BADGE_SIZE}px;
  height: ${BADGE_SIZE}px;
  left: ${ICON_SIZE - BADGE_SIZE}px;
  top: 0;
  z-index: 1;
  &:hover + ${IconWrapper} {
    transition: all ${TRANSITION_SPEED_MS} ease-in;
    background-color: ${Core.surface.subdued};
    padding: ${getSpacing("1x")};
  }
`;

const bounce = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
  45% {
    transform: scale(0.94);
    opacity: 1;
  }
  50%, 100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const IconCountBadge = styled(Row).attrs({
  align: "center",
  justify: "center",
})`
  border-radius: ${BADGE_SIZE * 2}px;
  background: ${Core.surface.default};
  border: 2px solid ${Core.text.inverse};
  padding: 5px;
  min-width: ${BADGE_SIZE}px;
  animation: ${bounce} 1s ease-in-out;
  animation-fill-mode: both;
`;

const StyledTypography = styled(Typography)`
  color: ${Core.text.inverse};
`;
